import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { useGravityToast } from '@/components/effects/GravityToast';
import { formatApiError } from '@/utils/api';
import GoldenSpiral from '@/components/effects/GoldenSpiral';
import { Eye, EyeOff, Mail, ArrowLeft, Loader2, Globe } from 'lucide-react';
import api from '@/utils/api';

export default function Auth() {
  // Modes: 'signin' (email+password together), 'register' (new account), 'magic-sent'
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [emailLang, setEmailLang] = useState('en');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const { login, register, user, checkAuth } = useAuth();
  const { t, lang } = useLang();
  const toast = useGravityToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const redirectTo = location.state?.from || '/';
  const magicToken = searchParams.get('token');

  useEffect(() => {
    setEmailLang(lang === 'ar' ? 'ar' : 'en');
  }, [lang]);

  // Magic link verification
  useEffect(() => {
    if (magicToken && !user) {
      (async () => {
        setLoading(true);
        try {
          const { data } = await api.get(`/auth/magic-link/verify?token=${magicToken}`);
          await checkAuth();
          toast.success(lang === 'ar' ? 'أهلاً بك!' : 'Welcome!');
          if (!data.has_password) {
            navigate('/auth/set-password', { state: { from: redirectTo }, replace: true });
          } else {
            navigate(redirectTo, { replace: true });
          }
        } catch (err) {
          setError(formatApiError(err.response?.data?.detail, err));
        }
        setLoading(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magicToken]);

  useEffect(() => {
    if (user && !magicToken) navigate(redirectTo, { replace: true });
  }, [user, navigate, redirectTo, magicToken]);

  if (user && !magicToken) return null;

  const tx = (en, ar) => (lang === 'ar' ? ar : en);

  if (magicToken) {
    return (
      <div data-testid="auth-page" className="min-h-screen flex items-center justify-center px-4 py-20 relative overflow-hidden">
        <div className="glass-card rounded-2xl p-8 md:p-10 w-full max-w-md relative z-10 text-center">
          {loading ? (
            <>
              <Loader2 className="w-8 h-8 text-sacred-gold animate-spin mx-auto mb-4" />
              <p className="text-warm-brown font-body">{tx('Verifying your magic link...', 'جاري التحقق من الرابط السحري...')}</p>
            </>
          ) : error ? (
            <>
              <p className="text-red-400 font-body mb-4">{error}</p>
              <button onClick={() => { window.location.href = '/auth'; }} className="gold-button px-6 py-2 rounded-full font-body text-sm" data-testid="back-to-signin-btn">
                {tx('Back to Sign In', 'عودة إلى تسجيل الدخول')}
              </button>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success(tx('Welcome back', 'أهلاً بعودتك'));
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err));
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email.trim(), password, username, displayName, emailLang);
      toast.success(tx('Welcome to the orbit', 'أهلاً بك في المدار'));
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err));
    }
    setLoading(false);
  };

  const handleMagicLink = async () => {
    setError('');
    if (!email.trim()) {
      setError(tx('Enter your email first.', 'أدخل بريدك الإلكتروني أولاً.'));
      return;
    }
    setMagicLoading(true);
    try {
      await api.post('/auth/magic-link', { email: email.trim(), email_lang: emailLang });
      setMode('magic-sent');
      toast.success(tx('Check your inbox!', 'تحقق من بريدك!'));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err));
    }
    setMagicLoading(false);
  };

  const switchToRegister = () => {
    // pre-suggest username from email
    if (email && !username) {
      const suggested = email.split('@')[0].toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20);
      setUsername(suggested);
    }
    setError('');
    setMode('register');
  };

  if (mode === 'magic-sent') {
    return (
      <div data-testid="auth-page" className="min-h-screen flex items-center justify-center px-4 py-20 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <GoldenSpiral size={400} className="animate-spiral-rotate" opacity={0.04} />
        </div>
        <div className="glass-card rounded-2xl p-8 md:p-10 w-full max-w-md relative z-10 text-center" data-testid="magic-link-sent">
          <Mail size={40} className="text-sacred-gold mx-auto mb-4" />
          <h2 className="text-2xl font-display text-heading mb-2">{tx('Check Your Email', 'تحقق من بريدك')}</h2>
          <p className="text-warm-brown font-body text-sm mb-6"><strong>{email}</strong></p>
          <p className="text-text-muted/50 text-xs font-body mb-6">{tx('The link expires in 15 minutes.', 'ينتهي الرابط خلال 15 دقيقة.')}</p>
          <button onClick={() => setMode('signin')} className="text-sacred-gold text-sm font-body hover:text-molten-gold" data-testid="use-different-email-btn">
            {tx('Back to Sign In', 'عودة إلى تسجيل الدخول')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="auth-page" className="min-h-screen flex items-center justify-center px-4 py-20 relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <GoldenSpiral size={400} className="animate-spiral-rotate" opacity={0.04} />
      </div>

      <div className="glass-card rounded-2xl p-8 md:p-10 w-full max-w-md relative z-10" data-testid="auth-form-container">
        {mode === 'signin' && (
          <>
            <h1 className="text-3xl font-display text-heading mb-2 text-center">{tx('Welcome back', 'أهلاً بعودتك')}</h1>
            <p className="text-text-muted text-sm font-body text-center mb-8">{tx('Sign in to continue to TariqWithin.', 'سجّل الدخول للمتابعة إلى TariqWithin.')}</p>

            {error && <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-body" data-testid="auth-error">{error}</div>}

            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label className="text-warm-brown text-xs font-body mb-1 block">{t('auth.email')}</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="auth-email" placeholder="email@example.com" />
              </div>
              <div>
                <label className="text-warm-brown text-xs font-body mb-1 block">{t('auth.password')}</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm pr-10" data-testid="auth-password" placeholder="******" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-dust-gray hover:text-heading">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full gold-button px-4 py-3 rounded-full font-body text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="auth-submit">
                {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                {tx('Sign In', 'تسجيل الدخول')}
              </button>
            </form>

            <div className="mt-5 pt-5 border-t border-sacred-gold/10 space-y-3 text-center">
              <button onClick={handleMagicLink} disabled={magicLoading} className="text-sacred-gold text-sm font-body hover:text-molten-gold flex items-center gap-1.5 mx-auto disabled:opacity-50" data-testid="magic-link-btn">
                {magicLoading ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                {tx('Send me a magic link instead', 'أرسل لي رابطاً سحرياً بدلاً')}
              </button>
              <p className="text-text-muted/70 text-xs font-body">
                {tx("Don't have an account?", 'ليس لديك حساب؟')}{' '}
                <button type="button" onClick={switchToRegister} className="text-sacred-gold hover:text-molten-gold font-body" data-testid="switch-to-register-btn">
                  {tx('Create one', 'أنشئ حساباً')}
                </button>
              </p>
            </div>
          </>
        )}

        {mode === 'register' && (
          <>
            <button onClick={() => { setMode('signin'); setError(''); }} className="flex items-center gap-1 text-text-muted text-xs font-body mb-4 hover:text-heading" data-testid="auth-back-btn">
              <ArrowLeft size={14} /> {tx('Back to Sign In', 'عودة إلى تسجيل الدخول')}
            </button>
            <h1 className="text-2xl font-display text-heading mb-2 text-center">{tx('Create your account', 'أنشئ حسابك')}</h1>
            <p className="text-text-muted text-sm font-body text-center mb-6">{tx('A password is required to unlock full access.', 'كلمة المرور مطلوبة لفتح الوصول الكامل.')}</p>

            {error && <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-body" data-testid="auth-error">{error}</div>}

            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="text-warm-brown text-xs font-body mb-1 block">{t('auth.email')}</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="auth-email" placeholder="email@example.com" />
              </div>
              <div>
                <label className="text-warm-brown text-xs font-body mb-1 block">{t('auth.displayName')}</label>
                <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} required className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="auth-display-name" placeholder={tx('Your name', 'اسمك')} />
              </div>
              <div>
                <label className="text-warm-brown text-xs font-body mb-1 block">{t('auth.username')}</label>
                <input type="text" value={username} onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} required minLength={3} maxLength={20} pattern="[a-zA-Z0-9_]+" className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="auth-username" placeholder="username" />
              </div>
              <div>
                <label className="text-warm-brown text-xs font-body mb-1 block">{t('auth.password')}</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm pr-10" data-testid="auth-password" placeholder="******" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-dust-gray hover:text-heading">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-warm-brown text-xs font-body mb-1 block flex items-center gap-1"><Globe size={12} /> {tx('Receive emails in', 'استلام الرسائل باللغة')}</label>
                <select value={emailLang} onChange={e => setEmailLang(e.target.value)} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="email-lang-select">
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                </select>
                <p className="text-text-muted/50 text-xs font-body mt-1">{tx('You can change this anytime in Settings.', 'يمكنك تغييرها في الإعدادات لاحقاً.')}</p>
              </div>
              <button type="submit" disabled={loading} className="w-full gold-button px-4 py-3 rounded-full font-body text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="auth-submit">
                {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                {tx('Create Account', 'إنشاء حساب')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
